import { useEffect, useState } from "react";

import { AnimatePresence } from "framer-motion";
import { v4 as uuidv4 } from "uuid";

import { usePageTitle } from "@/atoms/page-title-atom";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { CustomMetricsProvider } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { MetricsExplorerStateProvider } from "@features/metrics-explorer";
import {
  MetricsContent,
  MinimizedTopBar,
  TileModal,
  TopBar,
} from "@features/metrics-explorer/components";
import { WarningMessage } from "@features/metrics-explorer/components/warning-message";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { useIntersectionObserver } from "@hooks/use-intersection-observer";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";
import { FeedbackForm } from "@shared/feedback-form";

const FEEDBACK_FORM_ID = "4440308f-9c0d-4bcc-9ee9-f1b89a0b54db";

export const MetricsExplorer = () => {
  const { user } = useUser();
  const [, setPageTitle] = usePageTitle();

  useEffect(() => {
    setPageTitle("Metrics Explorer");
  }, [setPageTitle]);

  useEffect(() => {
    prettyLogEvent.visited({
      page: Pages.METRICS_EXPLORER,
      user,
    });
  }, [user]);

  return (
    <CustomMetricsProvider>
      <MetricsExplorerStateProvider>
        <MetricsExplorerContent />
      </MetricsExplorerStateProvider>
    </CustomMetricsProvider>
  );
};

export const MetricsExplorerContent = () => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [isTileModalOpen, setIsTileModalOpen] = useState(false);

  const [topLevelFiltersContainerElRef, isMinimizedTopBarVisible] =
    useIntersectionObserver();

  const { tiles, isMetadataLoading, setTiles } = useMetricsExplorerContext();

  if (isMetadataLoading) {
    return <LoadingSlide />;
  }

  return (
    <>
      <div className="bg-v2-primary min-h-[100%] text-v2-primary">
        <AnimatePresence>
          {isMinimizedTopBarVisible && <MinimizedTopBar />}
        </AnimatePresence>
        <div ref={topLevelFiltersContainerElRef}>
          <TopBar />
        </div>
        <WarningMessage />
        <MetricsContent
          onAddTile={() => {
            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onClick",
              additionalActionInfo: "Open tile modal",
            });

            setIsTileModalOpen(true);
          }}
          onQuickSelect={(value) => {
            const newTiles = value.metrics.map((value, idx) => ({
              id: uuidv4(),
              name: value.name,
              metricId: value.metricId,
              attributionModel: null,
              accountingMode: value.accountingMode ?? null,
              attributionWindow: null,
              compareDateRange: null,
              filterQuery: null,
              filters: value.filters,
              advancedSearch: null,
              active: idx === 0,
              visible: true,
              index: idx,
            }));

            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onClick",
              additionalActionInfo: "Quick select metric",
              data: {
                newTiles,
                pageContext: { tiles },
              },
            });

            setTiles(newTiles);
          }}
        />
      </div>
      <FeedbackForm formId={FEEDBACK_FORM_ID} />
      {isTileModalOpen && (
        <TileModal
          isOpen={isTileModalOpen}
          onClose={() => {
            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onClick",
              additionalActionInfo: "Close tile modal",
            });

            setIsTileModalOpen(false);
          }}
          onSubmit={(value) => {
            const newTiles = [
              ...tiles,
              {
                id: uuidv4(),
                name: value.name,
                metricId: value.metricId,
                attributionModel: value.attributionModel,
                accountingMode: value.accountingMode,
                attributionWindow: value.attributionWindow,
                compareDateRange: value.compareDateRange,
                filterQuery: value.filterQuery,
                filters: value.filters,
                advancedSearch: value.advancedSearch,
                active: tiles.length === 0,
                visible: true,
                index: tiles.length,
              },
            ];

            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onChange",
              additionalActionInfo: "Add tile",
              data: {
                newTiles,
                pageContext: { tiles },
              },
            });

            setTiles(newTiles);
            setIsTileModalOpen(false);
          }}
        />
      )}
    </>
  );
};
